import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import useSurveyApi from './config';
import { useAuth } from '../../context/AuthContext';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';
import Button from '../../components/ui/Button';
import TemplateStartDrawer from './TemplateStartDrawer';

const Container = styled.div``;

export default function SurveysPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const history = useHistory();

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, ids: [], name: '' });
  const [deleting, setDeleting] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);

  const { fetching, columns: baseColumns, data, pagination, _delete } = useSurveyApi(queryParams);

  const handleDelete = useCallback((row) => {
    setDeleteConfirm({ isOpen: true, ids: [row.id], name: row.title });
  }, []);

  const handleRowsDelete = useCallback((ids) => {
    setDeleteConfirm({ isOpen: true, ids, name: `${ids.length} ${t('survey.title').toLowerCase()}` });
  }, [t]);

  const handleDeleteConfirm = useCallback(async () => {
    setDeleting(true);
    await _delete(deleteConfirm.ids);
    setDeleteConfirm({ isOpen: false, ids: [], name: '' });
    setDeleting(false);
  }, [_delete, deleteConfirm.ids]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const handleRowClick = useCallback((row) => {
    history.push(`/surveys/${row.id}`);
  }, [history]);

  const actionOptions = useMemo(() => {
    const options = [
      { type: 'details', label: t('survey.detail'), onClick: (row) => history.push(`/surveys/${row.id}`) },
    ];
    if (canWrite('SV')) {
      options.push(
        { type: 'edit', label: t('survey.edit'), onClick: (row) => { if (row.status === 'draft') history.push(`/surveys/${row.id}`); }, condition: (row) => row.status === 'draft' },
        { type: 'divider' },
        { type: 'delete', label: t('survey.delete'), onClick: handleDelete, condition: (row) => row.status === 'draft' },
      );
    }
    return options;
  }, [t, canWrite, handleDelete, history]);

  const columns = useMemo(() => {
    if (actionOptions.length === 0) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            return <OptionsMenu actions={actionOptions} row={row} />;
          },
        },
      },
    ];
  }, [baseColumns, actionOptions, data]);

  return (
    <Container>
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('survey.deleteTitle')}
        message={t('survey.deleteMessage', { name: deleteConfirm.name })}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({ isOpen: false, ids: [], name: '' })}
        loading={deleting}
      />

      {useMemo(() => (
        <DataTable
          title={t('survey.title')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord={canWrite('SV')}
          // Spec 2026-10-16 S6 — şablon ana yol: birincil "Şablondan Başlat", ikincil "Boş Anket".
          newButtonLabel={t('survey.template.start')}
          onNewButtonClick={() => setTemplateOpen(true)}
          toolbarActions={canWrite('SV') ? (
            <Button variant="outline" onClick={() => history.push('/surveys/create')}>
              {t('survey.blankSurvey')}
            </Button>
          ) : undefined}
          onRowsDelete={canWrite('SV') ? handleRowsDelete : undefined}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
          onRowClick={handleRowClick}
        />
      ), [
        t, fetching, columns, data, pagination, canWrite,
        handleRowsDelete, filterList,
        handleFilterConfirm, handleDataStateChange, handleRowClick, history,
      ])}

      <TemplateStartDrawer open={templateOpen} onClose={() => setTemplateOpen(false)} />
    </Container>
  );
}
